import { getState, UniDispatch } from '../../store/types';
import superjson from 'superjson';
import { DataBlockValues } from '../../components/measurements/common';
import { Permissions } from '@lmt/shared-types';
import { FormType } from '../../components/global-forms/GlobalForms.types';
import { enabledFormTypes, FormInfo } from './types';
import { UnsavedChangesActions } from './reducer';
import { UseFormReturn } from 'react-hook-form';
import {
  isEmpty,
  isObjectEmpty,
} from '../../components/measurements/copy-datablock-field/common';

export const getValuesByFormId = (formId: number) => {
  const values = getState().unsavedChanges.globalForms.find(
    (form) => form.id === formId,
  )?.values;
  if (values) {
    return superjson.deserialize<{ [key: string]: any }>(values);
  }
  return undefined;
};

export const getStateByFormId = (formId: number) => {
  return getState().unsavedChanges.globalForms.find(
    (form) => form.id === formId,
  )?.form.state;
};

export const getDataBlockValuesByFormId = (formId: number) => {
  const values = getState().unsavedChanges.globalForms.find(
    (form) => form.id === formId,
  )?.values;
  if (!values) {
    return;
  }
  return superjson.deserialize<DataBlockValues>(values);
};

function checkIfUserHasPermissions(
  formType: FormType,
  permissions: Permissions,
) {
  if (
    [
      'createDataBlock',
      'createDataBlockStatistic',
      'editDataBlockStatistic',
    ].includes(formType)
  ) {
    return permissions.dataBlockManagement;
  } else {
    return true;
  }
}

const formIsEnabled = (formInfo: FormInfo, permissions: Permissions) => {
  return (
    !!formInfo.values &&
    enabledFormTypes.includes(formInfo.form.type) &&
    checkIfUserHasPermissions(formInfo.form.type, permissions)
  );
};

export const getFormsWithUnsavedChanges = (permissions: Permissions) => {
  return getState().unsavedChanges.globalForms.filter((form) =>
    formIsEnabled(form, permissions),
  );
};

export function saveFormUnsavedChangesToState(
  formId: number,
  form: UseFormReturn<any, any>,
  dispatch: UniDispatch,
) {
  const dirtyFieldKeys = Object.keys(form.formState.dirtyFields);
  if (dirtyFieldKeys.length > 0) {
    const values = form.getValues();
    const filteredEntries = Object.entries(values).filter(([key]) =>
      dirtyFieldKeys.includes(key),
    );
    const unsavedValues = Object.fromEntries(filteredEntries);
    dispatch(
      UnsavedChangesActions.globalFormValuesChanged({
        id: formId,
        values: unsavedValues,
      }),
    );
  } else {
    dispatch(UnsavedChangesActions.globalFormValuesRemoved(formId));
  }
}

export function saveUnsavedDataBlockValuesFromFormToState(
  formId: number,
  unsavedValues: DataBlockValues,
  showDetails: boolean,
  dispatch: UniDispatch,
) {
  if (
    isObjectEmpty(unsavedValues.fieldValues) &&
    isObjectEmpty(unsavedValues.sharedValues)
  ) {
    dispatch(UnsavedChangesActions.globalFormValuesRemoved(formId));
  } else {
    dispatch(
      UnsavedChangesActions.globalFormValuesChanged({
        id: formId,
        values: unsavedValues,
        showDetails: showDetails,
      }),
    );
  }
}

export function saveUnsavedDataBlockValuesToState(
  tabId: string,
  type: 'update_patient_block' | 'update_data_block',
  id: number,
  unsavedValues: DataBlockValues,
  showDetails: boolean,
  dispatch: UniDispatch,
) {
  if (
    !isObjectEmpty(unsavedValues.fieldValues) ||
    !isObjectEmpty(unsavedValues.sharedValues)
  ) {
    console.log('save data block', tabId, type, id, showDetails);
    dispatch(
      UnsavedChangesActions.dataBlockCreated({
        tabId,
        type,
        id,
        showDetails,
        values: superjson.serialize(unsavedValues),
      }),
    );
  }
}

export function removeDataBlockFromState(
  tabId: string,
  type: 'update_patient_block' | 'update_data_block',
  id: number,
  dispatch: UniDispatch,
) {
  dispatch(
    UnsavedChangesActions.dataBlockRemoved({
      tabId,
      type,
      id,
    }),
  );
}

export function clearUnsavedChangesState(
  dispatch: UniDispatch,
  permissions: Permissions,
) {
  // remove forms without unsaved changes or permissions
  const removedFormIds = getState()
    .unsavedChanges.globalForms.filter(
      (form) => !formIsEnabled(form, permissions),
    )
    .map((form) => form.id);

  for (const formId of removedFormIds) {
    dispatch(UnsavedChangesActions.globalFormClosed(formId));
  }
  // remove templates with closed tabs
  const currentTabIds = getState().navigation.tabs.map((tab) => tab.id);
  const templateTabIds = (
    getState().unsavedChanges.documentTemplates ?? []
  ).map((template) => template.tabId);
  for (const templateTabId of templateTabIds) {
    if (
      !currentTabIds.includes(templateTabId) ||
      !permissions.documentTemplateManagement
    ) {
      dispatch(UnsavedChangesActions.documentTemplateRemoved(templateTabId));
    }
  }
  // remove data blocks with closed tabs
  const dataBlocks = getState().unsavedChanges.dataBlocks;
  for (const dataBlock of dataBlocks) {
    if (
      !currentTabIds.includes(dataBlock.tabId) ||
      !permissions.dataBlockManagement
    ) {
      dispatch(
        UnsavedChangesActions.dataBlockRemoved({
          tabId: dataBlock.tabId,
          type: dataBlock.type,
          id: dataBlock.id,
        }),
      );
    }
  }
}

export function getDictDiff(
  dict1: { [key: string]: any },
  dict2: { [key: string]: any },
): { [key: string]: any } {
  let result: { [key: string]: any } = {};
  for (const key in dict1) {
    if (!dict2.hasOwnProperty(key)) {
      result[key] = undefined;
    } else if (JSON.stringify(dict1[key]) !== JSON.stringify(dict2[key])) {
      result[key] = dict2[key];
    }
  }
  for (const key in dict2) {
    if (!dict1.hasOwnProperty(key) && !isEmpty(dict2[key])) {
      result[key] = dict2[key];
    }
  }
  return result;
}

export function resetFormValuesWhenExpandedStateChanges(
  isExpanded: boolean,
  miniForm: UseFormReturn<any, any>,
  expandedForm: UseFormReturn<any, any>,
) {
  if (isExpanded) {
    const dirtyFieldKeys = Object.keys(miniForm.formState.dirtyFields);
    const miniFormValues = miniForm.getValues() as any;
    for (const key in miniFormValues) {
      expandedForm.setValue(key as any, miniFormValues[key], {
        shouldDirty: dirtyFieldKeys.includes(key),
        shouldValidate: false,
      });
    }
    miniForm.reset();
  } else {
    const expandedFormValues = expandedForm.getValues() as any;
    const dirtyFieldKeys = Object.keys(expandedForm.formState.dirtyFields);
    for (const key in expandedFormValues) {
      miniForm.setValue(key as any, expandedFormValues[key], {
        shouldDirty: dirtyFieldKeys.includes(key),
        shouldValidate: false,
      });
    }
    expandedForm.reset();
  }
}

export function getShowDetailsFromState(formId: number) {
  return (
    getState().unsavedChanges.globalForms.find((form) => form.id === formId)
      ?.showDetails ?? false
  );
}

export function getDataBlockInfoFromState(
  tabId: string,
  type: 'update_patient_block' | 'update_data_block',
  id: number,
) {
  const datablock = getState().unsavedChanges.dataBlocks.find(
    (datablock) =>
      datablock.tabId === tabId &&
      datablock.id === id &&
      datablock.type === type,
  );
  if (!datablock) {
    return undefined;
  }
  return {
    values: superjson.deserialize<DataBlockValues>(datablock.values),
    showDetails: datablock.showDetails,
  };
}

export function saveDocumentTemplateToState(
  tabId: string,
  templateId: number | null,
  content: string,
  dispatch: UniDispatch,
) {
  dispatch(
    UnsavedChangesActions.documentTemplateCreated({
      tabId,
      templateId,
      content,
    }),
  );
}

export function removeDocumentTemplateFromState(
  tabId: string,
  dispatch: UniDispatch,
) {
  dispatch(UnsavedChangesActions.documentTemplateRemoved(tabId));
}

export function getDocumentTemplateFromState(
  tabId: string,
  templateId: number | null,
) {
  return getState().unsavedChanges.documentTemplates?.find(
    (template) =>
      template.tabId === tabId && template.templateId === templateId,
  );
}
